// ════════════════════════════════════════════════════════════════════════════
//  ManDana — Today screen (the woven day timeline)
//  ----------------------------------------------------------------------------
//  Exports two pieces consumed by md-app.jsx:
//    • <TodayHeader>  pinned, frosted top panel = <HeroNext> (countdown + sun arc)
//                     stacked over <WeekBar> (the Sat→Fri day strip)
//    • <TodayScreen>  the scrolling schedule: prayers, events, sunrise marker and
//                     free-time gaps laid out in clock order, with a "now" line.
//
//  Now-line model — two modes:
//    • moving line (sizeByDur on, viewing today): cards are height-scaled by
//      duration, so the line can sit at the TRUE clock position. mdNowY()
//      interpolates a pixel Y from the measured card boxes; the line is an
//      absolutely-positioned overlay carrying [data-now-anchor].
//    • discrete line (uniform card heights): a <NowLine> row is spliced into the
//      list between the cards that straddle the current moment.
//  Either way the earliest-starting card at the current moment is tagged
//  [data-now-card], along with any current prayer card; the app scrolls to the
//  topmost of them. The now element carries [data-now-anchor] for visibility tests.
// ════════════════════════════════════════════════════════════════════════════
const { Glass, MDI, mdFmt, mdEnd, mdHM, mdRgba, mdIsLight, mdVivid, mdBackdropCSS,
  mdDateForOffset, mdHijri, mdHijriParts, mdOffsetForDate, mdOverlaps, mdWin, mdTodayCol, useMdDismissLayer: useEscape,
  mdDayKey, mdRecordTitle, mdPrayerWindowEnd } = window;
const { useState: useStateT, useRef: useRefT, useEffect: useEffectT, useLayoutEffect: useLayoutEffectT } = React;

// Overlap detection (mdOverlaps) and the seam-chip clock formatter (mdWin) live in
// the framework-free app/md-overlap.js, so the model can be unit-tested on its own
// (see overlap.tests.html). This view just consumes the result.

/* The one rendering of the next prayer's name and clock. It appears inside the
   hero normally and, while the day panel is folded away, floats over the board
   from md-app.jsx. Keeping the arithmetic here means those two places cannot
   disagree at midnight or while location is still being resolved. */
function PrayerCountdown({ accent, next, nowSec, action = null, compact = false, contentVisible = true }) {
  const light = mdIsLight();
  let cd = compact ? '—:—' : '—:—:—';
  if (next) {
    let remain = next.min * 60 - nowSec;
    if (remain < 0) remain += 24 * 3600;   // wraps to tomorrow's first prayer
    const hh = Math.floor(remain / 3600);
    const mm = Math.floor(remain % 3600 / 60);
    const ss = remain % 60;
    cd = compact
      ? `${hh}:${String(mm).padStart(2, '0')}`
      : `${hh}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  }
  /* ── THE GROUP IS THE CONTROL, WHEN THERE IS ONE TO OFFER ──────────────────
     `action` is the sidebar toggle, and it used to be an 18px square standing
     beside all of this: the smallest target on the screen, deciding which half
     of the app you are looking at, with a group of text beside it that says the
     same thing and does nothing when pressed. So the toggle is handed the whole
     group instead — it renders as the <button>, wearing the marker and the
     layout below, and calls back with its own glyph for the one place the
     arrangement wants it. One element: a click handler on this div would be a
     control no keyboard could reach, and a button layered over it would be a
     second tab stop for the same act. Where there is no action there is no
     button — a group that does nothing must not look like a control. */
  const group = (style, body) => (action
    ? React.cloneElement(action, { 'data-prayer-countdown': '', style, wrap: body })
    : <div data-prayer-countdown="" style={style}>{body(null)}</div>);
  if (compact) {
    return group({ display: 'inline-flex', alignItems: 'center',
      gap: 0, height: 36, whiteSpace: 'nowrap' }, (glyph) => (
      <>
        {glyph}
        <div className="md-display" aria-hidden={!contentVisible}
          style={{ height: 36, display: 'flex', alignItems: 'center', color: ink(0.64),
          fontSize: 16, lineHeight: 1, visibility: contentVisible ? 'visible' : 'hidden',
          fontVariantNumeric: 'tabular-nums', letterSpacing: 0, textShadow: 'none' }}>{cd}</div>
      </>));
  }
  return group({ display: 'inline-block', whiteSpace: 'nowrap' }, (glyph) => (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: 13 }}>
        {glyph}
        <div className="md-label" aria-hidden={!contentVisible}
          style={{ color: ink(0.5), fontSize: 11.5, fontWeight: 400,
            visibility: contentVisible ? 'visible' : 'hidden' }}>
          {next ? next.name : 'Locating'}
        </div>
      </div>
      <div className="md-display" aria-hidden={!contentVisible}
        style={{ color: ink(1), fontSize: 23, lineHeight: 1.05, marginTop: 2,
        visibility: contentVisible ? 'visible' : 'hidden',
        fontVariantNumeric: 'tabular-nums', letterSpacing: 0.5,
        textShadow: light ? 'none' : `0 2px 18px ${mdRgba(accent, 0.35)}` }}>
        {cd.slice(0, cd.lastIndexOf(':'))}
        <span style={{ fontSize: 13.7, fontWeight: 500, color: ink(0.5),
          letterSpacing: 0.3, marginLeft: 4, textShadow: 'none',
          verticalAlign: 1 }}>{cd.slice(cd.lastIndexOf(':') + 1)}</span>
      </div>
    </>));
}

/* ── Next-prayer hero: live Sun Arc + countdown only ── */
function HeroNext({ accent, next, nowSec, isToday = true, onFind,
  sidebarToggle = null, children }) {
  const SunArc = window.SunArc;
  const light = mdIsLight();

  // `next` is null until a location is known (first run, before the edge lookup
  // answers). PrayerCountdown keeps the card's shape with placeholders instead
  // of collapsing or throwing.
  // No `glow` on the hero: it carries the same depth as a resting record card — the
  // global halo, and nothing else. It used to add the per-card aura on top, which
  // read as lit-from-within against the frosted header and made the one permanent
  // card on screen shout. Glow is reserved for "now" (PrayerBlock/EventCard),
  // where it means something.
  //
  // In DARK the fill is transparent, not Glass's default white film. The week strip
  // right below is not a card — it is the header's own frosted panel, so it reads as
  // true black; the hero laying ~2.4% white over that same panel made it a grey
  // rectangle sitting on black. The glass is still glass: stroke, top sheen, halo and
  // the 24px corner all remain, and now it actually transmits what is behind it
  // instead of frosting it. LIGHT keeps the default white film — there the header is
  // near-white and a transparent hero would dissolve into it.
  //
  // `children` — the date row — rides INSIDE this card, under a full-bleed hairline,
  // the way a settings list stacks rows in one container. It used to float below the
  // card as a bare tracked-caps line on the frosted panel, which left it looking like
  // a caption nothing owned. As a row of this card it inherits the card's edges, its
  // tap target is the full width, and the month grid it opens has something to open
  // INSIDE — the expansion grows the card instead of pushing loose content around.
  // Hence padding moves off the Glass and onto each section: the divider has to reach
  // both edges, and it cannot do that from inside a padded box.
  return (
    <div style={{ position: 'relative', margin: '2px 0 0' }}>
    <Glass radius={24} alpha={0.10} tint={light ? undefined : 'transparent'}
    style={{ padding: 0 }}>
      <div style={{ padding: '12px 18px 6px' }}>
        {/* countdown only on today — its vertical space is reserved on other days so the card keeps a constant height */}
        <PrayerCountdown accent={accent} next={next} nowSec={nowSec}
          contentVisible={isToday} action={sidebarToggle} />
        {/* daylight trajectory — live sun on today, static preview otherwise */}
        <div style={{ marginTop: 2 }}>
          <SunArc accent={accent} nowSec={nowSec} preview={!isToday} />
        </div>
      </div>
      {children != null && <>
        {/* the row rule. Light gets a darker hairline than dark gets a lighter one —
            on the near-transparent dark card a 0.10 white line is already the
            brightest edge inside the card, while light needs ink to register at all. */}
        <div aria-hidden="true" style={{ height: 1, background: ink(light ? 0.07 : 0.09) }} />
        {children}
      </>}
    </Glass>
      {onFind &&
      <div style={{ position: 'absolute', top: 6, insetInlineEnd: 6, zIndex: 3,
        display: 'flex', alignItems: 'center' }}>
        <button aria-label="Search" aria-haspopup="dialog"
          title={`Search (${window.MD_FIND_KEY || '⌘K'})`}
          onClick={onFind}
          style={{ width: 38, height: 38, borderRadius: '50%', border: 'none', background: 'transparent',
            display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
          {window.MdFindMark && <window.MdFindMark color={ink(light ? 0.5 : 0.62)} size={19} />}
        </button>
      </div>}
    </div>);

}

/* ── weekly day strip — tap a day to view it, swipe to change weeks ── */
const MD_DOW_COLS = ['Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const MD_MON_L_LOCAL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/* shared circle treatment for a day number, used by both the week strip and the
   expanded month grid so the selected/today states read identically:
     • selected & today    → accent fill
     • selected, not today → solid ink pill (inverse) — the day you're viewing
     • today, not selected → accent numeral + accent ring — the real "today" */
function mdDayCircle(isSel, isToday, light, accent) {
  let bg = 'transparent', numCol = ink(0.82), fw = 550, shadow = 'none', bd = 'none';
  if (isSel && isToday) {
    bg = accent; numCol = onAcc(); fw = 750;
  } else if (isSel) {
    bg = light ? '#0d0e12' : '#ffffff';
    numCol = light ? '#ffffff' : '#0d0e12'; fw = 750;
  } else if (isToday) {
    numCol = mdRgba(accent, 1); fw = 750;
    bd = `1px solid ${mdRgba(accent, light ? 0.4 : 0.55)}`;
  }
  return { bg, numCol, fw, shadow, bd };
}

const MD_MON_S_LOCAL = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* Width of a .md-label string as the browser will draw it — measured through a
   COPY OF THE SPAN THAT WILL DRAW IT, placed beside the original so it inherits
   the same cascade, sized, and taken out again.

   This used to be a canvas `measureText` against a font string assembled from
   the probe's computed style, and a font string is not enough. The date cells
   are set in `font-variant-numeric: tabular-nums` so the day number does not
   jitter as the week is stepped through, tabular figures in Inter are wider
   than the proportional ones, and no CSS font shorthand can carry a numeric
   variant — so six digits of "MON 31 AUG 2026" each measured a fraction narrow
   and the line was given a tier it could not draw. Letter-spacing had the same
   shape of problem and was being added back by hand, one term per character.
   A clone carries all of it, exactly, including the family that really resolved.

   The fallback estimate errs WIDE, which may only ever pick a shorter date tier
   early — never a longer one that then clips. */
function mdLabelW(text, probe) {
  const s = String(text == null ? '' : text);
  const host = probe && probe.parentNode;
  if (!host) return s.length * 8.4;
  let ghost = null;
  try {
    ghost = probe.cloneNode(false);
    ghost.textContent = s;
    /* Free of every constraint the real cell puts on it — this asks how wide
       the string WANTS to be, which is the question the tiers are about. */
    ghost.setAttribute('aria-hidden', 'true');
    Object.assign(ghost.style, { position: 'absolute', visibility: 'hidden',
      whiteSpace: 'nowrap', maxWidth: 'none', width: 'auto', overflow: 'visible',
      opacity: '1', transition: 'none', pointerEvents: 'none', inset: 'auto' });
    host.appendChild(ghost);
    const width = ghost.getBoundingClientRect().width;
    /* happy-dom and any headless layout answer 0 for every box; a 0 here would
       silently claim that every tier fits. Fall back rather than lie. */
    return width > 0 ? width : s.length * 8.4;
  } catch (e) {
    return s.length * 8.4;
  } finally {
    if (ghost && ghost.parentNode) ghost.parentNode.removeChild(ghost);
  }
}

/** The longest date form whose widest cell fits, given each tier's measured
    width. Tier 0 is the fullest and the last is the floor — below it the CSS
    ellipsis is the net, and it must be reached holding the shortest string.
    A label exactly as wide as its cell FITS: both numbers come out of the same
    layout pass, so treating equality as overflow drops a tier on every cell
    that was sized to its own content. */
function mdDateTier(widths, cellW) {
  const list = Array.isArray(widths) ? widths : [];
  for (let t = 0; t < list.length; t += 1) if (list[t] <= cellW) return t;
  return list.length ? list.length - 1 : 0;
}

/*  `strip` is the Sat→Fri row of day circles. It belongs to schedule editing, where
    you are moving records between days and need every day of the week in reach at all
    times. On Today it was permanent furniture for an occasional act, so the home
    screen sets strip={false}: the date line stays, the month grid still opens from
    it, and the day you are on is now spelled out in the date itself rather than
    inferred from a highlighted circle. */
/*  `inset` says "I am rendering as a row INSIDE a card" (Today, under the hero) as
    opposed to standing on the bare frosted panel (schedule editing). It buys the row
    the card's own gutter so the date lines up with the countdown above it, and it
    gives the month grid the same gutter so the grid reads as the card's contents
    rather than something the card is sitting on top of. */
function WeekBar({ accent, selectedOffset = 0, onSelectDay, onSwipeWeek, strip = true, inset = false }) {
  const light = mdIsLight();
  // Today's own Sat→Fri column, not a fixed 6. Offsets are counted from today,
  // so this is what lines the strip's dates up under their true weekday labels.
  const todayCol = mdTodayCol();
  const weekIndex = Math.floor((selectedOffset + todayCol) / 7);
  const sel = mdDateForOffset(selectedOffset);

  // ── month-view expansion ──
  // `expanded` is local to each WeekBar instance, so the timeline header and the
  // grid edit view each get their own month view for free.
  const [expanded, setExpanded] = useStateT(false);
  // which month the grid shows (independent of the selected day, so you can page
  // ahead/back without selecting). Kept in sync with the selected day's month.
  const [cursor, setCursor] = useStateT(() => ({ y: sel.year, m: sel.m }));
  useEffectT(() => { setCursor({ y: sel.year, m: sel.m }); }, [sel.year, sel.m]);
  const stepMonth = (dir) => setCursor((c) => {
    let m = c.m + dir, y = c.y;
    if (m < 0) { m = 11; y -= 1; } else if (m > 11) { m = 0; y += 1; }
    return { y, m };
  });

  // slide the strip when the week changes
  const gridRef = useRefT(null);
  const prevWi = useRefT(weekIndex);
  useEffectT(() => {
    if (prevWi.current === weekIndex) return;
    const dir = weekIndex > prevWi.current ? 1 : -1;
    prevWi.current = weekIndex;
    const el = gridRef.current;
    if (!el) return;
    el.style.transition = 'none';
    el.style.transform = `translateX(${dir * 28}px)`;
    el.style.opacity = '0';
    requestAnimationFrame(() => requestAnimationFrame(() => {
      el.style.transition = 'transform .34s cubic-bezier(.22,.61,.36,1), opacity .26s ease';
      el.style.transform = 'translateX(0)';
      el.style.opacity = '1';
    }));
  }, [weekIndex]);

  // pointer-drag → swipe weeks (collapsed) or page months (expanded);
  // also suppresses the tap that ends a drag
  const drag = useRefT({ x: 0, active: false, moved: false });
  const onDown = (e) => {drag.current = { x: e.clientX, active: true, moved: false };};
  const onMove = (e) => {if (drag.current.active && Math.abs(e.clientX - drag.current.x) > 8) drag.current.moved = true;};
  const onUp = (e) => {
    if (!drag.current.active) return;
    const dx = e.clientX - drag.current.x;
    drag.current.active = false;
    if (Math.abs(dx) <= 42) return;
    if (expanded) stepMonth(dx < 0 ? 1 : -1);
    else if (onSwipeWeek) onSwipeWeek(dx < 0 ? 1 : -1);
  };
  const onCancel = () => {drag.current.active = false;};

  // tapping a day selects it and gently collapses back to the week (you've picked
  // your day → see the day). The drag guard prevents a swipe-end from selecting.
  const pickDay = (offset) => {
    if (drag.current.moved || !onSelectDay) return;
    onSelectDay(offset);
    setExpanded(false);
  };

  /* ── the day-nav row ─────────────────────────────────────────────────────
     [prev-day ‹] [ greg | calendar toggle | hijri ] [› next-day]. The cluster is
     a 1fr-auto-1fr grid, so the two date cells are always the same width and the
     calendar button sits at the exact centre of the row in BOTH states; expanding
     cross-fades the cell contents (dates become month title + pager) instead of
     moving anything. The day steppers fade out but keep their width — the centre
     never shifts. */
  const rowRef = useRefT(null);
  const clusterRef = useRefT(null);
  const probeRef = useRefT(null);   // a live .md-label span to copy the font from
  const calBtnRef = useRefT(null);
  const hParts = mdHijriParts(selectedOffset);
  /* The date line must survive long month names (SEPTEMBER), long Hijri months
     (Rabiʿ al-Awwal) and two-digit days on a 320px phone. Four tiers, and the
     longest that fits wins — the years go first (they barely change), the weekday
     last. Ellipsis in the cells is only the last-resort net below tier 3. */
  const gregTiers = [
    `${sel.dow} ${sel.d} ${sel.monLong} ${sel.year}`,
    `${sel.dow} ${sel.d} ${sel.mon} ${sel.year}`,
    `${sel.dow} ${sel.d} ${sel.mon}`,
    `${sel.d} ${sel.mon}`];
  const hijriTiers = [
    `${hParts.day} ${hParts.month} ${hParts.year}`,
    `${hParts.day} ${hParts.short} ${hParts.year}`,
    `${hParts.day} ${hParts.short}`,
    `${hParts.day} ${hParts.short}`];
  const [tier, setTier] = useStateT(0);
  useLayoutEffectT(() => {
    const cluster = clusterRef.current, probe = probeRef.current;
    if (!cluster || !probe) return;
    const pick = () => {
      /* What each date cell really gets: the cluster minus the toggle column
         and the two gaps beside it, halved. MEASURED, not assumed — the button
         is the middle `auto` column, so its width is whatever it renders as,
         and a guess here is a guess about every date on the screen. */
      const gaps = (parseFloat(getComputedStyle(cluster).columnGap) || 0) * 2;
      const toggle = calBtnRef.current ? calBtnRef.current.getBoundingClientRect().width : 30;
      const cellW = (cluster.clientWidth - (toggle || 30) - gaps) / 2;
      const widths = gregTiers.map((greg, i) =>
        Math.max(mdLabelW(greg, probe), mdLabelW(hijriTiers[i], probe)));
      const t = mdDateTier(widths, cellW);
      setTier((prev) => (prev === t ? prev : t));
    };
    pick();
    const ro = new ResizeObserver(pick);
    ro.observe(cluster);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(pick);
    return () => ro.disconnect();
  }, [gregTiers.join('\n'), hijriTiers.join('\n')]);
  // opening always starts the grid on the selected day's month, not a stale cursor
  const toggleCalendar = () => {
    if (!expanded) setCursor({ y: sel.year, m: sel.m });
    setExpanded(!expanded);
  };
  const stepDay = (dir) => { if (onSelectDay) onSelectDay(selectedOffset + dir); };
  /* Expanding hides the day steppers and collapsing hides the pager. If the
     control going hidden holds focus, hand it to the calendar toggle — the one
     control visible in both states — rather than stranding it on a ghost. */
  useEffectT(() => {
    const ae = document.activeElement;
    if (ae && rowRef.current && rowRef.current.contains(ae) && ae.closest('[aria-hidden="true"]'))
      calBtnRef.current && calBtnRef.current.focus();
  }, [expanded]);

  const cells = MD_DOW_COLS.map((dow, i) => {
    const offset = weekIndex * 7 + (i - todayCol);
    return { dow, offset, n: mdDateForOffset(offset).d, isToday: offset === 0, isSel: offset === selectedOffset };
  });

  // ── month grid model: leading blanks (Sat-start), each day of the cursor month,
  //    then trailing blanks so every row holds 7 cells (keeps space-between aligned) ──
  const lead = (new Date(cursor.y, cursor.m, 1).getDay() + 1) % 7;   // Sat→Fri column of the 1st
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const monthCells = [];
  for (let i = 0; i < lead; i++) monthCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const offset = mdOffsetForDate(cursor.y, cursor.m, d);
    monthCells.push({ d, offset, hijri: mdHijriParts(offset), isToday: offset === 0, isSel: offset === selectedOffset });
  }
  while (monthCells.length % 7 !== 0) monthCells.push(null);
  const monthRows = [];
  for (let i = 0; i < monthCells.length; i += 7) monthRows.push(monthCells.slice(i, i + 7));

  /* The whole date line — Gregorian and Hijri — is set as a label, not display type:
     uppercase, tracked out, and sized to the prayer name above the countdown, so the
     two read as one family of quiet headers rather than two competing titles. */
  const dateType = { fontSize: 11.5, fontWeight: 400 };
  // every column cell is a fixed 30px box (the day circle's width); with
  // space-between this distributes the 7 columns edge-to-edge, flush with the
  // event cards on both sides — identical for the week strip and the month grid.
  const CELL = 30;
  const rowFlex = { display: 'flex', justifyContent: 'space-between' };
  /* the card's own gutter when this is a row inside the hero card; zero when the bar
     stands on the bare panel and the header's 16px padding is already the gutter */
  const PAD = inset ? 18 : 0;

  /* 30x30 hit areas on a 14px line, same negative-margin pattern as the classic
     pager: the targets bleed into the row's own padding without inflating the
     flex line, so the row stays 44px and nothing drops when it re-roles. */
  const navBtn = { width: 30, height: 30, margin: '-8px 0', flexShrink: 0, display: 'grid',
    placeItems: 'center', background: 'none', border: 'none', cursor: 'pointer', padding: 0 };
  // a cell of the cluster: two contents stacked on one grid area, cross-faded
  const cellText = { gridArea: '1/1', ...dateType, color: ink(1), whiteSpace: 'nowrap',
    maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis',
    fontVariantNumeric: 'tabular-nums', transition: 'opacity .2s ease',
    /* A span at opacity 0 still forms a stacking context and paints ABOVE the
       in-flow pager it shares a cell with — without this it eats the pager's
       clicks while invisible. The dates never own a gesture; taps fall through. */
    pointerEvents: 'none' };
  const dayNavRow = (
    <>
      <button aria-label="Previous day" onClick={() => stepDay(-1)}
        aria-hidden={expanded} tabIndex={expanded ? -1 : 0}
        style={{ ...navBtn, opacity: expanded ? 0 : 1, pointerEvents: expanded ? 'none' : 'auto',
          transition: 'opacity .24s ease' }}>
        <span style={{ display: 'grid', transform: 'rotate(180deg)' }}>{MDI.chevron(ink(0.55), 14)}</span>
      </button>
      <div ref={clusterRef} dir="ltr" style={{ flex: 1, minWidth: 0, display: 'grid',
        gridTemplateColumns: 'minmax(0,1fr) auto minmax(0,1fr)', alignItems: 'center',
        columnGap: 6, unicodeBidi: 'isolate' }}>
        {/* left cell — the Gregorian date; the cursor month takes its place while expanded */}
        <div style={{ display: 'grid', justifyItems: 'end', minWidth: 0 }}>
          <span ref={probeRef} className="md-label" aria-hidden={expanded}
            style={{ ...cellText, opacity: expanded ? 0 : 1 }}>{gregTiers[tier]}</span>
          <span className="md-label" aria-hidden={!expanded}
            style={{ ...cellText, opacity: expanded ? 1 : 0 }}>
            {tier >= 2 ? `${MD_MON_S_LOCAL[cursor.m]} ${cursor.y}` : `${MD_MON_L_LOCAL[cursor.m]} ${cursor.y}`}</span>
        </div>
        {/* the toggle — the fixed centre of the row in both states. The accent chip
            behind the glyph is the open state: a plain icon between two dates would
            otherwise read as the separator it replaced. */}
        <button ref={calBtnRef} onClick={toggleCalendar}
          aria-label={expanded ? 'Close calendar' : 'Open calendar'} aria-expanded={expanded}
          style={{ ...navBtn, flexShrink: 0 }}>
          <span style={{ display: 'grid', padding: 4, borderRadius: 9,
            background: expanded ? mdRgba(accent, 0.14) : 'transparent', transition: 'background .24s ease' }}>
            {MDI.calendar(expanded ? mdRgba(accent, 0.95) : ink(0.55), 15)}
          </span>
        </button>
        {/* right cell — the Hijri date; the month pager takes its place while expanded */}
        <div style={{ display: 'grid', justifyItems: 'start', minWidth: 0 }}>
          <span className="md-label" aria-hidden={expanded}
            style={{ ...cellText, opacity: expanded ? 0 : 1 }}><LTR>{hijriTiers[tier]}</LTR></span>
          <div aria-hidden={!expanded}
            style={{ gridArea: '1/1', position: 'relative', display: 'flex', alignItems: 'center', gap: 2,
              opacity: expanded ? 1 : 0, pointerEvents: expanded ? 'auto' : 'none',
              transition: 'opacity .24s ease' }}>
            <button aria-label="Previous month" onClick={() => stepMonth(-1)} tabIndex={expanded ? 0 : -1}
              style={navBtn}>
              <span style={{ display: 'grid', transform: 'rotate(180deg)' }}>{MDI.chevron(ink(0.55), 14)}</span>
            </button>
            <button aria-label="Next month" onClick={() => stepMonth(1)} tabIndex={expanded ? 0 : -1}
              style={navBtn}>
              {MDI.chevron(ink(0.55), 14)}
            </button>
          </div>
        </div>
      </div>
      <button aria-label="Next day" onClick={() => stepDay(1)}
        aria-hidden={expanded} tabIndex={expanded ? -1 : 0}
        style={{ ...navBtn, opacity: expanded ? 0 : 1, pointerEvents: expanded ? 'none' : 'auto',
          transition: 'opacity .24s ease' }}>
        {MDI.chevron(ink(0.55), 14)}
      </button>
    </>);

  return (
    <div style={{ marginTop: inset ? 0 : 16 }}>
      {/* In-card the row carries the same gutter as everything else, and enough height
          for the date to sit in air rather than press on the hairline above it — at
          15/15 it also clears 44pt, which is what the row is as a tap target.
          The vertical padding MUST stay symmetric: the row is one flex line of 30px
          controls bleeding on negative margins, and any asymmetry drops the centred
          calendar toggle off the date line. Space owed BELOW the row is therefore a
          margin (bare panel) or the grid's own padding (in-card), never this
          row's padding. */}
      <div ref={rowRef} style={{ position: 'relative', display: 'flex', alignItems: 'center',
        padding: inset ? `15px ${PAD}px` : 0,
        marginBottom: inset ? 0 : (strip || expanded ? 15 : 2) }}>
        {dayNavRow}

      </div>

      {/* ── collapsed: the single-week strip (spread edge-to-edge, label over circle) ── */}
      {strip &&
      <div style={{ display: 'grid', gridTemplateRows: expanded ? '0fr' : '1fr',
        transition: 'grid-template-rows .42s cubic-bezier(.22,.61,.36,1)' }}>
        <div style={{ overflow: 'hidden', minHeight: 0 }}>
          <div onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp}
            onPointerCancel={onCancel} onPointerLeave={onCancel}
            style={{ touchAction: 'pan-y', opacity: expanded ? 0 : 1, transition: 'opacity .26s ease' }}>
            <div ref={gridRef} style={rowFlex}>
              {cells.map((d, i) => {
                const labelCol = d.isSel ? ink(0.95) : d.isToday ? mdRgba(accent, 0.95) : ink(0.45);
                const c = mdDayCircle(d.isSel, d.isToday, light, accent);
                return (
                  <button key={i} onClick={() => pickDay(d.offset)}
                    style={{ width: CELL, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                    <span style={{ color: labelCol, fontSize: 11,
                      fontWeight: d.isSel || d.isToday ? 700 : 500, letterSpacing: 0.5 }}>{d.dow}</span>
                    <div className="md-display" style={{ width: 30, height: 30, borderRadius: '50%', display: 'grid', placeItems: 'center',
                      backgroundColor: c.bg, color: c.numCol, border: c.bd,
                      fontSize: 15.5, fontWeight: c.fw, fontVariantNumeric: 'tabular-nums',
                      transition: 'background-color .18s ease, box-shadow .18s ease, color .18s ease',
                      boxShadow: c.shadow }}>{d.n}</div>
                  </button>);
              })}
            </div>
          </div>
        </div>
      </div>}

      {/* ── expanded: the full month grid, Gregorian over Hijri (same edge-to-edge spread) ── */}
      <div style={{ display: 'grid', gridTemplateRows: expanded ? '1fr' : '0fr',
        transition: 'grid-template-rows .42s cubic-bezier(.22,.61,.36,1)' }}>
        <div style={{ overflow: 'hidden', minHeight: 0 }}>
          {/* In-card, the grid takes the same gutter as the date row and a real floor,
              so the month sits INSIDE the card rather than running off its bottom edge.
              (Comment lives out here, not in the style block: the a11y gate's tag
              scanner treats an apostrophe inside a JSX attribute brace as an open
              string, desyncs on the tag, and then blames this element for an onClick
              that belongs to one further down.) */}
          <div onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp}
            onPointerCancel={onCancel} onPointerLeave={onCancel}
            style={{ touchAction: 'pan-y', opacity: expanded ? 1 : 0, transition: 'opacity .3s ease .08s',
              padding: inset ? `0 ${PAD}px 14px` : '0 0 4px' }}>
            {/* day-of-week header — same 7 columns, flush with the grid below */}
            <div style={{ ...rowFlex, marginBottom: 8 }}>
              {MD_DOW_COLS.map((dow, i) => (
                <span key={i} style={{ width: CELL, textAlign: 'center', color: ink(0.4), fontSize: 11, fontWeight: 600, letterSpacing: 0.5 }}>{dow}</span>))}
            </div>
            {monthRows.map((row, ri) => (
              <div key={ri} style={{ ...rowFlex, marginBottom: 2 }}>
                {row.map((d, ci) => {
                  if (!d) return <span key={ci} style={{ width: CELL }} />;   // blank padding cell
                  const c = mdDayCircle(d.isSel, d.isToday, light, accent);
                  // Hijri sub-label: the month short-name when a Hijri month *begins*
                  // here (a quiet calendar landmark), otherwise just the day number.
                  const hStart = d.hijri.isMonthStart;
                  return (
                    <button key={ci} onClick={() => pickDay(d.offset)}
                      style={{ width: CELL, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                        background: 'none', border: 'none', padding: '3px 0 5px', cursor: 'pointer' }}>
                      <div className="md-display" style={{ width: 30, height: 30, borderRadius: '50%', display: 'grid', placeItems: 'center',
                        backgroundColor: c.bg, color: c.numCol, border: c.bd,
                        fontSize: 15.5, fontWeight: c.fw, fontVariantNumeric: 'tabular-nums',
                        transition: 'background-color .18s ease, box-shadow .18s ease, color .18s ease',
                        boxShadow: c.shadow }}>{d.d}</div>
                      <span style={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.1, whiteSpace: 'nowrap',
                        fontSize: hStart ? 9.5 : 10.5, fontWeight: hStart ? 700 : 500, letterSpacing: hStart ? 0.1 : 0.2,
                        color: hStart ? mdRgba(accent, 0.95) : (d.isSel || d.isToday ? ink(0.55) : ink(0.4)) }}>
                        {hStart ? d.hijri.short : d.hijri.day}</span>
                    </button>);
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>);

}

/* ── pinned top header: hero + weekly strip, fixed while the day list scrolls ── */
function TodayHeader({ accent, next, nowSec, innerRef,
  selectedOffset = 0, onSelectDay, onSwipeWeek, onFind, onSettings, onProjects, onDatabases, onCrafts,
  boards = null, openBoardId = null, onOpenBoard, onRenameBoard, onDeleteBoard,
  sidebarToggle = null, insetSafeArea = true, viewportFixed = false }) {
  const light = mdIsLight();
  const isToday = selectedOffset === 0;
  /* ONE FROST, AND NOTHING TO DECIDE. This used to have a second, opaque value
     for a panel that floated over the board with nothing solid behind it — and
     it was chosen by `insetSafeArea`, which is really about who pays the
     device's top inset. Two unrelated things on one flag was a coincidence that
     held until the board went full-bleed; then the geometry flipped, took the
     colour with it, and the desktop's hero card went solid.
     The answer was not a better flag. Every sheet now stands on the app's own
     opaque ground (see the panel in md-app.jsx), so there is no case left where
     this has to conceal a board — the cards scroll under it and show through,
     which is the whole effect. Only the header is filled; the timeline below
     stays unfilled. */
  /* DARK TAKES ITS COLOUR FROM THE BACKDROP, it does not remember one.
     The constant this replaced was measured against hsl(228 18% 5%) — a good
     match, and a fact about one moment. bgBlackness then shipped at 1, the backdrop
     became pure black, and this panel stayed where it was: a lighter rectangle
     on the void, full panel width, square behind a hero card whose own fill is
     transparent by design (see the note in HeroNext). The token is set by the
     app shell from the same gradient and blackness Background draws with, so
     the two cannot disagree at any position of the slider. The old constant
     survives as the var() fallback, which is what a mount with no shell above
     it — every surface test in tests/ — renders with.
     Light is untouched: its frost is deliberately a shade DARKER than its
     near-white backdrop, and blackness is a dark-only tweak. */
  const frost = light ? 'rgba(244,245,247,0.94)' : mdBackdropCSS(0.72);
  // solid frosted panel through the content, soft fade only at the bottom edge
  const bg = `linear-gradient(180deg, ${frost} 0%, ${frost} calc(100% - 14px), transparent 100%)`;
  return (
    <header ref={innerRef} data-md-today-header="true" style={{ position: viewportFixed ? 'fixed' : 'absolute', top: 0, left: 0, right: 0, zIndex: 30,
      // The 56px this used to hard-code was clearance for the mockup's DRAWN
      // status bar. There isn't one any more, so this takes the real inset: 0 in
      // a browser tab, where the browser's own chrome is already above, and the
      // notch when installed. On a desktop panel env() is 0 and 14 is the floor.
      // A phone in ordinary Safari is different: its visual viewport already
      // starts below the browser's status chrome, so another 14px reads as a
      // second status-bar gutter. Keep a quiet 6px rest there (the Hero adds
      // its own 2px optical nudge), while a real notch/PWA inset still wins.
      // The expression itself is --md-top-inset in index.html, because the board's
      // toolbar has to start at the same line as this card and used to work it out
      // separately — see the token's comment for how that came apart on an iPad.
      padding: `${viewportFixed ? 'max(var(--md-safe-top), 6px)'
        : insetSafeArea ? 'var(--md-top-inset)' : '14px'} 16px 14px`, pointerEvents: 'none',
      backdropFilter: 'blur(18px) saturate(150%)', WebkitBackdropFilter: 'blur(18px) saturate(150%)',
      background: bg }}>
      <div style={{ pointerEvents: 'auto' }}>
        {/* The document's one h1. It is the day being viewed, which is what this screen
            IS — visually the date already reads as the title, so the heading is
            placed on it rather than invented above it. */}
        <h1 style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden',
          clip: 'rect(0 0 0 0)', clipPath: 'inset(50%)', whiteSpace: 'nowrap', margin: 0 }}>
          {(() => { const dt = mdDateForOffset(selectedOffset);
            return `${dt.dow} ${dt.d} ${dt.monLong} ${dt.year}${isToday ? ' — today' : ''}`; })()}
        </h1>
        {/* The date is the hero card's second row, under a hairline — one card that
            says "this is the moment, and this is the day it belongs to". Opening it
            grows the card downward and the month appears inside it.
            No week strip here — the date line names the day. The strip stays in
            schedule editing, where moving a record to another day is the whole job. */}
        <HeroNext accent={accent} next={next} nowSec={nowSec} isToday={isToday}
          onFind={onFind} onSettings={onSettings} onProjects={onProjects} onDatabases={onDatabases}
          onCrafts={onCrafts} sidebarToggle={sidebarToggle}
          boards={boards} openBoardId={openBoardId} onOpenBoard={onOpenBoard}
          onRenameBoard={onRenameBoard} onDeleteBoard={onDeleteBoard}>
          <nav aria-label="Date">
            <WeekBar accent={accent} selectedOffset={selectedOffset} strip={false} inset
            onSelectDay={onSelectDay} onSwipeWeek={onSwipeWeek} />
          </nav>
        </HeroNext>
      </div>
    </header>);

}

/* ── completed/"done" emerald — one consistent green for the completed state.
   Pale-swatch values from the design system's Emerald life-class, theme-aware. ── */
const MD_DONE = { light: '#0FA877', dark: '#34D399' };
const mdDoneGreen = () => mdIsLight() ? MD_DONE.light : MD_DONE.dark;

/* ── the prayer tile's ground — the droplet mark rests on the app's own accent
   (the same purple the FAB, Sun Arc and Now pill wear), lifted toward white at
   the top and settled toward black at the base so the ramp tracks whatever
   accent the theme picks. ── */
const mdPrayerTileBg = (accent) =>
  `linear-gradient(140deg, color-mix(in srgb, ${accent} 80%, #fff) 0%, ${accent} 48%, color-mix(in srgb, ${accent} 68%, #000) 100%)`;

/* ── the 40×40 icon tile. While "done" it becomes the green check AND a tap
   target that toggles the card back to not-done (stopPropagation so it never
   trips the card's long-press → grid gesture). ── */
function DoneTile({ done, onToggle, bg, bd, children }) {
  const common = { width: 40, height: 40, borderRadius: 13, flexShrink: 0, display: 'grid', placeItems: 'center',
    background: bg, border: `0.75px solid ${bd}`,
    transition: 'background-color .22s ease, border-color .22s ease, transform .14s ease' };
  /* Was a plain <div> until it had already been marked done, which meant the only
     way to COMPLETE something was the swipe gesture on SwipeRow. A swipe is not
     available to a keyboard, a switch, or most screen-reader users, and it is
     undiscoverable besides — the tile looks tappable and did nothing. Both states
     are a button now, and aria-pressed carries the state. */
  return (
    <button aria-label={done ? 'Mark not done' : 'Mark done'} aria-pressed={!!done}
      onPointerDown={(e) => { e.stopPropagation(); e.currentTarget.style.transform = 'scale(0.86)'; }}
      onPointerUp={(e) => { e.stopPropagation(); e.currentTarget.style.transform = 'scale(1)'; }}
      onPointerLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      onClick={(e) => { e.stopPropagation(); onToggle && onToggle(); }}
      style={{ ...common, padding: 0, cursor: 'pointer' }}>
      {children}
    </button>);
}

/* ── the green left-edge band drawn on a completed card. Lives inside the Glass
   (which clips to its 20px radius) so it hugs the rounded left corner. ── */
function doneBand(g) {
  return <span aria-hidden="true" style={{ position: 'absolute', left: 0, top: 11, bottom: 11, width: 3.5, zIndex: 2,
    borderRadius: '0 3px 3px 0', background: g, boxShadow: `0 0 9px ${mdRgba(g, 0.55)}` }} />;
}

/* ── swipe wrapper — two opposite gestures on one card ───────────────────────
   • RIGHT-swipe → mark done. As the card slides right it reveals a frosted-emerald
     track on the left whose check glyph DRAWS IN with the swipe; crossing the
     threshold fires a haptic and commits on release.
   • LEFT-swipe  → reveal trailing actions (Reschedule · Delete) as two frosted
     glass chips that sit beneath the card's right edge. Snaps open past ~42% and
     rests there; tap a chip to act, tap the card to close.
   The gesture is axis-locked on the first move so it never fights vertical scroll
   or the card's long-press → grid edit. Done cards skip the right-swipe but can
   still be left-swiped to reveal actions. Actions are only enabled when an
   onReschedule / onDelete handler is supplied (so locked prayers don't get them). */
const MD_ACT_W = 150;     // total trailing-action reveal width (two chips)
function SwipeRow({ done, onToggle, radius = 20, accent, onReschedule, onDelete, fine = false, children }) {
  // the revealed action drawer is dismissible, so Escape must close it too
  const light = mdIsLight();
  const g = light ? MD_DONE.light : MD_DONE.dark;
  const rose = light ? '#E11D48' : '#F43F5E';
  const hasActions = !!(onReschedule || onDelete);
  const rootRef = useRefT(null);
  const cardRef = useRefT(null);
  const trackRef = useRefT(null);
  const chipRef = useRefT(null);
  const checkRef = useRefT(null);
  const actRef = useRefT(null);
  const rescRef = useRefT(null);
  const delRef = useRefT(null);
  const [open, setOpen] = useStateT(false);
  const openRef = useRefT(false);
  const st = useRefT({ on: false, axis: null, x0: 0, y0: 0, dx: 0, tx: 0, mode: 'none', crossed: false, armed: false, pid: null, base: 0 });
  const THRESH = 88;

  // RIGHT-swipe (complete) visual — `raw` is the rightward drag distance
  const completeVisual = (raw) => {
    let v = Math.max(0, raw);
    if (v > THRESH) v = THRESH + (v - THRESH) * 0.32;     // resistance past the commit point
    v = Math.min(v, THRESH + 48);
    const prog = Math.min(1, raw / THRESH);
    if (cardRef.current) cardRef.current.style.transform = `translateX(${v}px)`;
    if (trackRef.current) { trackRef.current.style.width = v + 'px'; trackRef.current.style.opacity = raw > 2 ? '1' : '0'; }
    if (chipRef.current) {
      chipRef.current.style.transform = `scale(${0.62 + 0.38 * prog})`;
      chipRef.current.style.background = prog >= 1 ? g : mdRgba(g, light ? 0.22 : 0.30);
      chipRef.current.style.boxShadow = prog >= 1 ? `0 4px 14px ${mdRgba(g, 0.5)}` : 'none';
    }
    if (checkRef.current) {
      checkRef.current.style.strokeDashoffset = String(Math.max(0, 26 * (1 - prog)));
      checkRef.current.setAttribute('stroke', prog >= 1 ? '#fff' : g);
    }
  };
  // LEFT-swipe (actions) visual — `tx` is the (negative) card offset
  const actionsVisual = (tx) => {
    let v = tx;
    if (v < -MD_ACT_W) v = -MD_ACT_W + (v + MD_ACT_W) * 0.3;   // resistance past full reveal
    v = Math.max(v, -MD_ACT_W - 40);
    if (cardRef.current) cardRef.current.style.transform = `translateX(${v}px)`;
    if (actRef.current) {
      const prog = Math.min(1, -tx / MD_ACT_W);
      actRef.current.style.opacity = String(Math.max(0, prog));
      actRef.current.style.pointerEvents = 'none';
    }
  };
  const releaseComplete = (commit) => {
    if (cardRef.current) { cardRef.current.style.transition = 'transform .34s cubic-bezier(.22,.61,.36,1)'; cardRef.current.style.transform = 'translateX(0)'; }
    if (trackRef.current) { trackRef.current.style.transition = 'width .34s cubic-bezier(.22,.61,.36,1), opacity .3s ease'; trackRef.current.style.width = '0px'; trackRef.current.style.opacity = '0'; }
    if (commit) { if (navigator.vibrate) try { navigator.vibrate(9); } catch (e) {} onToggle && onToggle(); }
  };
  // settle the actions drawer open or closed
  const settle = (toOpen) => {
    openRef.current = toOpen;
    setOpen(toOpen);
    if (rescRef.current) rescRef.current.style.flexGrow = '1';
    if (delRef.current) { delRef.current.style.flexGrow = '1'; delRef.current.style.opacity = '1'; }
    if (cardRef.current) { cardRef.current.style.transition = 'transform .34s cubic-bezier(.22,.61,.36,1)'; cardRef.current.style.transform = `translateX(${toOpen ? -MD_ACT_W : 0}px)`; }
    if (actRef.current) {
      actRef.current.style.transition = 'opacity .3s ease';
      actRef.current.style.opacity = toOpen ? '1' : '0';
      actRef.current.style.pointerEvents = toOpen ? 'auto' : 'none';
    }
    if (toOpen && navigator.vibrate) try { navigator.vibrate(7); } catch (e) {}
  };
  const fireAction = (fn) => { settle(false); if (fn) setTimeout(fn, 120); };
  useEscape(() => settle(false), open);

  /* ── the gesture ends where the POINTER ends, not where the card is ────────
     The row around this card also watches for a heavy press, and after 420ms
     that press opens the edit grid — an overlay, over a day column that stays
     mounted underneath. So the pointerup that finishes the press lands on the
     GRID and never reaches this element: the gesture record survived it, still
     `on`, still holding the press point as its origin. A finger sends no moves
     once it is lifted and never noticed. A MOUSE sends one on every hover, so
     coming back to the day resumed a swipe nobody was making — the pressed card
     slid out from under the cursor, complete track showing (2026-08-19).
     Heard on the WINDOW, in the capture phase, the end arrives wherever the
     release happened and before anything on the way down can swallow it. */
  const endRef = useRefT(null);      // the current render's ender
  const winEnd = useRefT(null);      // the listener actually attached
  const detachEnd = () => {
    if (!winEnd.current) return;
    window.removeEventListener('pointerup', winEnd.current, true);
    window.removeEventListener('pointercancel', winEnd.current, true);
    winEnd.current = null;
  };
  useEffectT(() => detachEnd, []);
  const onDown = (e) => {
    if (done && !hasActions) return;          // locked-done card with no actions: inert
    st.current = { on: true, axis: null, x0: e.clientX, y0: e.clientY, dx: 0, tx: openRef.current ? -MD_ACT_W : 0,
      mode: 'none', crossed: false, armed: false, pid: e.pointerId, base: openRef.current ? -MD_ACT_W : 0,
      longThresh: (cardRef.current ? cardRef.current.offsetWidth : 360) * 0.5 };
    if (cardRef.current) cardRef.current.style.transition = 'none';
    if (trackRef.current) trackRef.current.style.transition = 'none';
    if (actRef.current) actRef.current.style.transition = 'none';
    detachEnd();
    // through a ref, so a re-render between press and release is not a stale close-over
    winEnd.current = (ev) => endRef.current && endRef.current(ev.type === 'pointercancel');
    window.addEventListener('pointerup', winEnd.current, true);
    window.addEventListener('pointercancel', winEnd.current, true);
  };
  const onMove = (e) => {
    const s = st.current; if (!s.on) return;
    /* Nothing is pressed, so this is a hover — the one thing a drag can never
       be. Only mouse and pen hover; a touch pointer reports 1 for the whole
       time it is in contact, and is left alone rather than trusted here. */
    if (e.buttons === 0 && e.pointerType !== 'touch') { endGesture(true); return; }
    const dx = e.clientX - s.x0, dy = e.clientY - s.y0;
    if (s.axis === null) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      s.axis = Math.abs(dx) > Math.abs(dy) + 2 ? 'x' : 'y';
      if (s.axis === 'x') { try { e.currentTarget.setPointerCapture(s.pid); } catch (err) {} }
    }
    if (s.axis !== 'x') return;
    e.preventDefault();
    s.dx = dx;
    const tx = s.base + dx;
    s.tx = tx;
    if (s.base === 0 && tx > 0) {
      // rightward from rest → complete (unless this card can't be completed)
      if (done || !onToggle) { if (cardRef.current) cardRef.current.style.transform = `translateX(${tx * 0.16}px)`; s.mode = 'rubber'; }
      else {
        s.mode = 'complete';
        if (!s.crossed && dx >= THRESH) { s.crossed = true; if (navigator.vibrate) try { navigator.vibrate(11); } catch (err) {} }
        else if (s.crossed && dx < THRESH) s.crossed = false;
        completeVisual(dx);
      }
    } else {
      // leftward, or returning from an open drawer → actions
      if (hasActions) {
        s.mode = 'actions';
        const raw = Math.min(0, tx);
        actionsVisual(raw);
        // pulling past the screen centre arms the primary action (Reschedule): the purple
        // chip takes over the whole drawer (the red Delete recedes), so a release opens it.
        if (onReschedule) {
          const armed = raw <= -s.longThresh;
          if (armed !== s.armed) {
            s.armed = armed;
            if (navigator.vibrate) try { navigator.vibrate(armed ? 12 : 6); } catch (err) {}
            if (rescRef.current) { rescRef.current.style.transition = 'flex-grow .2s ease'; rescRef.current.style.flexGrow = armed ? '99' : '1'; }
            if (delRef.current) { delRef.current.style.transition = 'flex-grow .2s ease, opacity .2s ease'; delRef.current.style.flexGrow = armed ? '0' : '1'; delRef.current.style.opacity = armed ? '0' : '1'; }
          }
        }
      }
      else { if (cardRef.current) cardRef.current.style.transform = `translateX(${Math.min(0, tx) * 0.16}px)`; s.mode = 'rubber'; }
    }
  };
  /* One ender for both endings. `cancelled` is the difference between a release
     — which commits whatever the drag reached — and an abandonment, which puts
     the card back where it was found. */
  const endGesture = (cancelled) => {
    detachEnd();
    const s = st.current; if (!s.on) return;
    const wasX = s.axis === 'x'; const mode = s.mode;
    s.on = false; s.axis = null;
    try { rootRef.current.releasePointerCapture(s.pid); } catch (err) {}
    if (!wasX) return;
    if (cancelled) {
      if (mode === 'complete' || mode === 'rubber') releaseComplete(false);
      else if (mode === 'actions') settle(openRef.current);
      return;
    }
    if (mode === 'complete') { releaseComplete(s.dx >= THRESH); return; }
    if (mode === 'rubber') { if (cardRef.current) { cardRef.current.style.transition = 'transform .3s cubic-bezier(.22,.61,.36,1)'; cardRef.current.style.transform = `translateX(${openRef.current ? -MD_ACT_W : 0}px)`; } return; }
    if (mode === 'actions') {
      // a long pull went straight to Reschedule; otherwise rest open past ~42% / closed
      if (s.armed && onReschedule) { fireAction(onReschedule); return; }
      const target = s.base === 0 ? (-s.tx >= MD_ACT_W * 0.42) : (s.tx <= -MD_ACT_W * 0.58);
      settle(target);
    }
  };
  endRef.current = endGesture;

  // a trailing action chip — frosted, icon-only (the glyph is expressive enough)
  const ActionChip = ({ col, fn, label, glyph, chipRefArg }) => (
    <button aria-label={label} ref={chipRefArg}
      onPointerDown={(e) => { e.stopPropagation(); e.currentTarget.style.transform = 'scale(0.9)'; }}
      onPointerUp={(e) => { e.stopPropagation(); e.currentTarget.style.transform = 'scale(1)'; }}
      onPointerLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      onClick={(e) => { e.stopPropagation(); fireAction(fn); }}
      style={{ flex: 1, minWidth: 0, display: 'grid', placeItems: 'center',
        border: 'none', cursor: 'pointer', padding: 0,
        background: mdRgba(col, light ? 0.16 : 0.26),
        transition: 'transform .12s ease, flex-grow .18s ease' }}>
      {glyph(col, 23)}
    </button>);

  /* ── the mouse's share of these gestures ───────────────────────────────────
     Everything above is a swipe, and a mouse cannot swipe. Rather than invent a
     drag-with-the-mouse (which fights text selection and reads as broken the
     first time it doesn't quite work), a pointer gets the same three actions as
     a small cluster that fades in on hover at the card's trailing edge.
     Gated on `fine` — a MOUSE, not a big screen. An iPad at 1180px keeps its
     swipes and never renders this, so no device carries both vocabularies.
     `hot` also follows focus, so the cluster is a real keyboard path to Done and
     Reschedule rather than three controls a Tab key falls into invisibly. */
  const [hot, setHot] = useStateT(false);
  const hoverBtn = (label, glyph, col, fn) => (
    <button key={label} aria-label={label}
      onPointerDown={(e) => { e.stopPropagation(); }}
      onClick={(e) => { e.stopPropagation(); if (openRef.current) settle(false); fn(); }}
      style={{ width: 30, height: 30, borderRadius: 9, border: 'none', background: 'none',
        display: 'grid', placeItems: 'center', cursor: 'pointer', padding: 0,
        transition: 'background .14s ease' }}
      onPointerEnter={(e) => e.currentTarget.style.background = mdRgba(col, light ? 0.16 : 0.24)}
      onPointerLeave={(e) => e.currentTarget.style.background = 'none'}>
      {glyph(col, 17)}
    </button>);

  return (
    <div ref={rootRef} style={{ position: 'relative', touchAction: 'pan-y' }}
      onPointerDown={onDown} onPointerMove={onMove}
      onPointerEnter={fine ? () => setHot(true) : undefined}
      onPointerLeave={fine ? () => setHot(false) : undefined}
      onFocus={fine ? () => setHot(true) : undefined}
      onBlur={fine ? (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setHot(false); } : undefined}>
      {/* emerald completion track — revealed to the LEFT as the card slides right */}
      <div ref={trackRef} aria-hidden="true" style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 0, opacity: 0,
        borderRadius: radius, overflow: 'hidden', zIndex: 0, display: 'flex', alignItems: 'center', paddingLeft: 16,
        background: light ? `linear-gradient(90deg, ${mdRgba(g, 0.20)}, ${mdRgba(g, 0.09)})` : `linear-gradient(90deg, ${mdRgba(g, 0.26)}, ${mdRgba(g, 0.11)})`,
        border: `0.75px solid ${mdRgba(g, light ? 0.32 : 0.4)}`, borderRight: 'none' }}>
        <span ref={chipRef} style={{ width: 30, height: 30, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center',
          background: mdRgba(g, light ? 0.22 : 0.30), transform: 'scale(0.62)',
          transition: 'transform .12s ease, background-color .15s ease, box-shadow .15s ease' }}>
          <svg aria-hidden="true" focusable="false" width="17" height="17" viewBox="0 0 24 24" fill="none">
            <path ref={checkRef} d="M5 12.5l4.2 4.2L19 7" stroke={g} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"
              style={{ strokeDasharray: 26, strokeDashoffset: 26 }} />
          </svg>
        </span>
      </div>
      {/* trailing actions — Delete (left) · Reschedule (right); revealed as the card slides left */}
      {hasActions &&
        <div ref={actRef} aria-hidden={!open} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: MD_ACT_W,
          opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none', zIndex: 0, display: 'flex', gap: 2,
          borderRadius: `0 ${radius}px ${radius}px 0`, overflow: 'hidden' }}>
          {onDelete && <ActionChip col={rose} fn={onDelete} label="Delete" glyph={MDI.trash} chipRefArg={delRef} />}
          {onReschedule && <ActionChip col={accent || (light ? '#7C68E6' : '#A78BFA')} fn={onReschedule} label="Reschedule" glyph={MDI.calendar} chipRefArg={rescRef} />}
        </div>}
      {/* the card itself — slides over whichever track is revealed */}
      <div ref={cardRef} style={{ position: 'relative', zIndex: 1, willChange: 'transform' }}>
        {children}
        {/* when the drawer is open, a quiet tap-catcher over the card closes it */}
        {open && <div onClick={(e) => { e.stopPropagation(); settle(false); }} aria-hidden="true"
          onPointerDown={(e) => e.stopPropagation()}
          style={{ position: 'absolute', inset: 0, zIndex: 5, cursor: 'pointer' }} />}
        {/* the pointer's action cluster — rides ON the card so it travels with any
            swipe, and sits above it so it stays clickable. Hidden entirely on
            touch: `fine` is false there and this never renders. */}
        {fine && (onToggle || onReschedule || onDelete) &&
          <div style={{ position: 'absolute', insetInlineEnd: 8, top: '50%', zIndex: 6,
            transform: `translateY(-50%) translateX(${hot ? 0 : 6}px)`,
            opacity: hot ? 1 : 0, pointerEvents: hot ? 'auto' : 'none',
            display: 'flex', alignItems: 'center', gap: 1, padding: 3, borderRadius: 12,
            transition: 'opacity .16s ease, transform .16s cubic-bezier(.22,.61,.36,1)',
            background: light ? 'rgba(255,255,255,0.9)' : 'rgba(28,30,38,0.9)',
            border: `0.75px solid ${ink(light ? 0.1 : 0.16)}`,
            backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
            boxShadow: light ? '0 6px 18px rgba(40,60,110,0.18)' : '0 6px 20px rgba(0,0,0,0.5)' }}>
            {onToggle && hoverBtn(done ? 'Mark not done' : 'Mark done', MDI.check, done ? ink(0.5) : g, onToggle)}
            {onReschedule && hoverBtn('Reschedule', MDI.calendar, accent || (light ? '#7C68E6' : '#A78BFA'), onReschedule)}
            {onDelete && hoverBtn('Delete', MDI.trash, rose, onDelete)}
          </div>}
      </div>
    </div>);
}

/* ── one timeline prayer block ── */
function PrayerBlock({ p, accent, isNext, isNow, isPast, prayed, reminder, sizeByDur, pxPerMin, pillRow, done, onToggleDone }) {
  // the window rule lives in md-theme.jsx and nowhere else
  const endMin = mdPrayerWindowEnd(p.min);
  const dur = endMin - p.min;
  const rowMinH = sizeByDur ? Math.round(10 + (dur || 0) * (pxPerMin || 1.6)) : undefined;
  const light = mdIsLight();
  const g = mdDoneGreen();
  return (
    <div style={{ borderRadius: 20, transition: 'box-shadow .3s cubic-bezier(.22,.61,.36,1)',
      boxShadow: isNow ? `0 8px 30px -6px ${mdRgba(accent, light ? 0.32 : 0.42)}, 0 0 20px ${mdRgba(accent, light ? 0.16 : 0.22)}` : 'none' }}>
    <Glass radius={20}
    tint={isNow ? mdRgba(accent, light ? 0.26 : 0.22) : isPast ? surf(0.05) : surf(0.07)}
    stroke={0.18}
    glow={isNow}
    inner={done ? doneBand(g) : undefined}
    style={{ padding: '13px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: rowMinH }}>
        <DoneTile done={done} onToggle={onToggleDone}
          bg={done ? mdRgba(g, light ? 0.16 : 0.20) : mdPrayerTileBg(accent)}
          bd={done ? mdRgba(g, light ? 0.5 : 0.6) : mdRgba(accent, 0.5)}>
          {/* riding 3px high, like the PWA icon: its droplet centers 8.4% above
              the canvas middle, and 8.4% of this 40px tile is 3px */}
          {done ? MDI.check(g, 18) : <span aria-hidden="true" style={{ display: 'inline-flex', transform: 'translateY(-3px)' }}>{MDI.droplet(18)}</span>}
        </DoneTile>
        <div style={{ flex: 1, minWidth: 0, opacity: done ? 0.5 : 1,
          transition: 'opacity .3s cubic-bezier(.22,.61,.36,1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: ink(1), fontSize: 17, fontWeight: 650 }}>{p.name}</span>
          </div>
          <div style={{ color: ink(0.72), fontSize: 12.5, marginTop: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
            <LTR><span>{mdFmt(p.t)} – {mdEnd(p.min, dur)}</span></LTR>
            {reminder && <span role="img" aria-label="Reminder set" style={{ display: 'inline-flex' }}>{MDI.bell(ink(0.55), 12)}</span>}
          </div>
        </div>
        <div style={{ color: ink(0.5), fontSize: 13, fontWeight: 600, flexShrink: 0,
          fontVariantNumeric: 'tabular-nums', opacity: pillRow ? 0 : (done ? 0.5 : 1),
          transition: 'opacity .3s cubic-bezier(.22,.61,.36,1)' }}>{mdHM(dur)}</div>
      </div>
    </Glass>
    </div>);

}

/* ── regular event card — icon tile colored by life-class (see design system) ──
   The icon and colour tables that used to sit here were a second copy of the one
   in md-compose.jsx, kept in step by hand. Both now read md-domain.ts. Note the
   fallbacks differ from a picker's on purpose: an event with no domain, or with
   one from a newer build, gets a clock and neutral ink rather than a stand-in
   life-class it does not belong to. */
function EventCard({ e, isPast, isNow, accent, sizeByDur, pxPerMin, pillRow, done, onToggleDone, onOpen }) {
  const dom = window.mdDomainById(e.id);
  const icon = (dom && MDI[dom.icon]) || MDI.clock;
  const light = mdIsLight();
  const col = dom ? window.mdDomainColor(e.id, light) : ink(0.6);
  const g = mdDoneGreen();
  // proportional timeline: card height tracks event duration (longer = taller)
  const rowMinH = sizeByDur ? Math.round(10 + (e.dur || 0) * (pxPerMin || 1.6)) : undefined;
  return (
    // when the now-line passes through this card it gets the same accent fill,
    // brighter stroke and glow as a "now" PrayerBlock, so both card types match
    <div style={{ borderRadius: 20, transition: 'box-shadow .3s cubic-bezier(.22,.61,.36,1)',
      boxShadow: isNow ? `0 8px 30px -6px ${mdRgba(accent, light ? 0.32 : 0.42)}, 0 0 20px ${mdRgba(accent, light ? 0.16 : 0.22)}` : 'none' }}>
    <Glass radius={20}
    tint={isNow ? mdRgba(accent, light ? 0.26 : 0.22) : isPast ? surf(0.05) : surf(0.07)}
    stroke={0.18}
    glow={isNow}
    inner={done ? doneBand(g) : undefined}
    style={{ padding: '13px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: rowMinH }}>
        <DoneTile done={done} onToggle={onToggleDone}
          bg={done ? mdRgba(g, light ? 0.16 : 0.20) : mdRgba(col, light ? 0.12 : 0.16)}
          bd={done ? mdRgba(g, light ? 0.5 : 0.6) : mdRgba(col, light ? 0.28 : 0.34)}>
          {done ? MDI.check(g, 18) : icon(col, 18)}
        </DoneTile>
        {/* The text block is a real <button> (like DoneTile and AllDayCard's title):
            the WHOLE card answers a tap, via the row's pointer handler — but a
            keyboard and a screen reader need a named, focusable target, and this is
            it. The icon tile beside it stays reserved for marking done. Negative
            vertical margin re-absorbs the card padding so the hit area is full height. */}
        <button onClick={onOpen} aria-label={`Open “${mdRecordTitle(e)}”`}
          style={{ flex: 1, minWidth: 0, textAlign: 'start', background: 'none', border: 'none',
            cursor: 'pointer', font: 'inherit', color: 'inherit', display: 'block',
            margin: '-13px 0', padding: '13px 0',
            opacity: done ? 0.5 : 1, transition: 'opacity .3s cubic-bezier(.22,.61,.36,1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* A record is created by opening it, so a card can be on screen before
                it has a first line to be called by. Until then it is "New Record",
                and the moment one is typed this tracks it, keystroke by keystroke. */}
            <span style={{ color: ink(1), fontSize: 16, fontWeight: 600,
              opacity: e.name ? 1 : 0.55 }}>{mdRecordTitle(e)}</span>
          </div>
          <div style={{ color: ink(0.6), fontSize: 12.5, marginTop: 2 }}>
            <LTR><span>{mdFmt(e.t)} – {mdEnd(e.min, e.dur)}</span></LTR>{e.loc ? ' · ' + e.loc : ''}
          </div>
        </button>
        <div style={{ color: ink(0.5), fontSize: 13, fontWeight: 600, flexShrink: 0,
          fontVariantNumeric: 'tabular-nums', opacity: pillRow ? 0 : (done ? 0.5 : 1),
          transition: 'opacity .3s cubic-bezier(.22,.61,.36,1)' }}>{mdHM(e.dur)}</div>
      </div>
    </Glass>
    </div>);

}

/* ── sunrise marker ── */
function SunMarker({ s }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '2px 6px 2px 10px', opacity: 0.6 }}>
      {MDI.sun(ink(0.7), 15)}
      <span style={{ color: ink(0.7), fontSize: 12 }}>{s.name} · <LTR>{mdFmt(s.t)}</LTR></span>
      <span style={{ flex: 1, height: 1, borderTop: '1px dotted rgba(255,255,255,0.25)' }} />
    </div>);

}

/* ── seam label — a whisper-quiet shared-time chip that sits at the seam where two
   overlapping cards layer. No red, no connector: the layered glass IS the signal;
   this just names the shared window in muted ink. Rendered inside the UPPER card's
   wrapper (which stacks above the lower one) so it reads over the seam. ── */
function SeamLabel({ s, e, onOpen }) {
  const light = mdIsLight();
  return (
    <div
      role="button" tabIndex={0} aria-label={`Overlapping window ${mdWin(s, e)}. Open the editor here.`}
      onPointerDown={(ev) => { ev.stopPropagation(); }}
      onClick={(ev) => { ev.stopPropagation(); onOpen && onOpen({ focalMin: s, x: ev.clientX, y: ev.clientY }); }}
      onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); ev.stopPropagation();
        onOpen && onOpen({ focalMin: s, x: 196, y: 470 }); } }}
      style={{ position: 'absolute', insetInlineEnd: 13, bottom: -10, zIndex: 4, display: 'inline-flex',
      alignItems: 'center', gap: 5, padding: '2.5px 9px 2.5px 7px', borderRadius: 999,
      cursor: onOpen ? 'pointer' : 'default',
      background: light ? 'rgba(255,255,255,0.88)' : 'rgba(26,28,36,0.86)',
      backdropFilter: 'blur(10px) saturate(140%)', WebkitBackdropFilter: 'blur(10px) saturate(140%)',
      border: light ? '0.75px solid rgba(40,60,110,0.10)' : '0.75px solid rgba(255,255,255,0.13)',
      boxShadow: light ? '0 2px 9px rgba(40,60,110,0.10)' : '0 2px 11px rgba(0,0,0,0.45)',
      fontSize: 10.5, fontWeight: 600, letterSpacing: 0.2, color: ink(0.55),
      fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
      transition: 'transform .14s ease' }}
      onPointerUp={(ev) => { ev.currentTarget.style.transform = 'scale(1)'; }}
      onPointerEnter={(ev) => { if (onOpen) ev.currentTarget.style.transform = 'scale(1.05)'; }}
      onPointerLeave={(ev) => { ev.currentTarget.style.transform = 'scale(1)'; }}>
      <span style={{ display: 'inline-flex', opacity: 0.75 }}>{MDI.overlap(ink(0.5), 12)}</span>
      <span>{mdWin(s, e)}</span>
    </div>);

}

/* ── current-time indicator — a "Now" label + line drawn in the timeline body ── */
function NowLine({ accent }) {
  return (
    <div data-now-anchor="" style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '5px 6px', margin: '1px 0' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: accent,
        boxShadow: `0 0 8px ${mdRgba(accent, 0.85)}` }} />
      <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase',
        color: accent, flexShrink: 0 }}>Now</span>
      <span style={{ flex: 1, height: 2, borderRadius: 2,
        background: `linear-gradient(90deg, ${accent}, ${mdRgba(accent, 0.12)})` }} />
    </div>);

}

/* ── bidi isolation for LTR runs ─────────────────────────────────────────────
   Clock times, Gregorian dates and the Hijri date are Latin-numeral runs that
   read left-to-right in any language. Placed directly in an RTL paragraph the
   bidi algorithm reorders them: "5:10 PM – 7:51 PM" comes out as
   "PM – 7:51 PM 5:10". For an app whose entire purpose is telling you when to
   pray, that is not cosmetic. <bdi> with an explicit dir pins the run and
   isolates it from its neighbours. In LTR it changes nothing. */
function LTR({ children, style }) {
  return <bdi dir="ltr" style={{ unicodeBidi: 'isolate', ...style }}>{children}</bdi>;
}

/* ── the screen ── */
/* ── free-time gap card — static summary of an open block ── */
function FreeTime({ mins, from, to, isNow, accent, sizeByDur, pxPerMin }) {
  // times can run past midnight (the after-Isha block ends at next-day Fajr), so wrap into 0–1439
  const t2 = (m) => { const mm = ((m % 1440) + 1440) % 1440; return mdFmt(`${String(Math.floor(mm / 60)).padStart(2, '0')}:${String(mm % 60).padStart(2, '0')}`); };
  const rowMinH = sizeByDur ? Math.round(10 + (mins || 0) * (pxPerMin || 1.6)) : undefined;
  return (
    <div style={{ borderRadius: 20, border: `1px dashed ${ink(0.13)}`, background: ink(0.025) }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 14px', minHeight: rowMinH }}>
        {/* Dome of the Rock mark for open time — frameless, sitting directly
            on the slot with no tile behind it. Decorative: the row already
            names itself for assistive tech, so the image stays silent
            (alt="", aria-hidden). 36 px tall keeps the old 40 px tile's
            rhythm in the row. */}
        <img src="/icons/freedom-dome.png" alt="" aria-hidden="true" draggable={false}
          style={{ display: 'block', flexShrink: 0, height: 36, width: 'auto', objectFit: 'contain' }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ color: ink(0.62), fontSize: 14, fontWeight: 550 }}>Freedom</span>
          </div>
          <div style={{ color: ink(0.42), fontSize: 12, marginTop: 1 }}><LTR>{t2(from)} – {t2(to)}</LTR></div>
        </div>
        <div style={{ color: ink(0.5), fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{mdHM(mins)}</div>
      </div>
    </div>);

}

/* ── interpolate the vertical position (px from the timeline top) of `nowMin`
   across the measured time-blocks, so the now-line sits at the true clock position ── */
function mdNowY(blocks, nowMin) {
  if (!blocks || !blocks.length) return null;
  const f = blocks[0], l = blocks[blocks.length - 1];
  if (nowMin <= f.start) return f.top;
  if (nowMin >= l.end) return l.bottom;
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    if (nowMin >= b.start && nowMin <= b.end)
      return b.top + (nowMin - b.start) / ((b.end - b.start) || 1) * (b.bottom - b.top);
    const nx = blocks[i + 1];
    if (nx && nowMin > b.end && nowMin < nx.start)
      return b.bottom + (nowMin - b.end) / ((nx.start - b.end) || 1) * (nx.top - b.bottom);
  }
  return null;
}

/* ── an All-Day Record — an untimed capture (note / task / idea). Lives on the
   shelf above Fajr. Swipe to complete; tap to open it in the compose sheet. ── */
function AllDayCard({ rec, light, accent, isDone, onToggle, onEdit, onDelete, onReschedule, fine }) {
  const col = rec.domain ? window.mdDomainColor(rec.domain, light) : ink(0.5);
  const g = mdDoneGreen();
  const preview = (rec.body || '').split('\n').map((s) => s.trim()).find(Boolean);
  const previewClean = preview ? preview.replace(/^(#{1,3} |[-*] |> |- \[[ xX]\] )/, '').replace(/[*`]/g, '').trim() : '';
  return (
    <SwipeRow done={isDone} onToggle={onToggle} radius={18} accent={accent} onDelete={onDelete} onReschedule={onReschedule} fine={fine}>
      {/* Glass is purely visual and forwards no handlers — an onClick given to it
          is silently dropped. The tappable region is a real <button> (like DoneTile
          above): keyboard-focusable, named, and actually wired. Its negative margin
          re-absorbs the card padding so the hit area still reaches the card edges. */}
      <Glass radius={18} tint={isDone ? mdRgba(g, light ? 0.1 : 0.14) : surf(0.06)} stroke={0.18}
        inner={isDone ? doneBand(g) : undefined} style={{ padding: '11px 13px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <DoneTile done={isDone} onToggle={onToggle}
            bg={isDone ? mdRgba(g, light ? 0.16 : 0.2) : (rec.domain ? mdRgba(col, light ? 0.12 : 0.16) : ink(0.06))}
            bd={isDone ? mdRgba(g, light ? 0.5 : 0.6) : (rec.domain ? mdRgba(col, light ? 0.28 : 0.34) : ink(0.12))}>
            {isDone ? MDI.check(g, 18)
              : (rec.domain ? window.mdDomainIcon(rec.domain, col, 18)
                : <span style={{ width: 9, height: 9, borderRadius: '50%', border: `1.7px solid ${ink(0.35)}` }} />)}
          </DoneTile>
          <button onClick={onEdit} aria-label={`Edit “${rec.name || 'Untitled'}”`}
            style={{ flex: 1, minWidth: 0, textAlign: 'start', background: 'none', border: 'none',
              cursor: 'pointer', font: 'inherit', color: 'inherit', display: 'block',
              margin: '-11px -13px -11px 0', padding: '11px 13px 11px 0' }}>
            <div style={{ color: ink(0.96), fontSize: 16, fontWeight: 600, display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden', lineHeight: 1.25, wordBreak: 'break-word' }}>
              {rec.name || 'Untitled'}</div>
            {previewClean &&
              <div style={{ color: ink(0.5), fontSize: 12.5, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {previewClean}</div>}
          </button>
        </div>
      </Glass>
    </SwipeRow>);
}

/* ── the All-Day shelf header + its records, shown above the first prayer ── */
function AllDaySection({ records, light, accent, done, dayOffset, onToggleDone, onEditRecord, onDeleteItem, onRescheduleItem, fine }) {
  if (!records || !records.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 3 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 4px 1px' }}>
        <h2 className="md-label" style={{ color: ink(0.55), fontSize: 11, fontWeight: 400, whiteSpace: 'nowrap', margin: 0 }}>All Day</h2>
        <span style={{ flex: 1, height: 1, borderRadius: 2, background: ink(0.1) }} />
        <span style={{ color: ink(0.4), fontSize: 11.5, fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>{records.length}</span>
      </div>
      {records.map((r) => {
        const isDone = !!done[mdDayKey(dayOffset) + '|' + r.uid];
        return <AllDayCard key={r.uid} rec={r} light={light} accent={accent} isDone={isDone}
          onToggle={() => onToggleDone && onToggleDone(r.uid)} onEdit={() => onEditRecord && onEditRecord(r)}
          onDelete={onDeleteItem ? () => onDeleteItem(r) : undefined}
          onReschedule={onRescheduleItem ? () => onRescheduleItem(r) : undefined} fine={fine} />;
      })}
    </div>);
}

function DayStartBreak() {
  return (
    <div data-screen-label="Start of day" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '2px 4px 7px' }}>
      <span style={{ flex: 1, height: 1, background: `linear-gradient(90deg, transparent, ${ink(0.16)})` }} />
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
        <span style={{ display: 'inline-flex', opacity: 0.85 }}>{MDI.star('#F59E0B', 10)}</span>
        <span style={{ color: ink(0.6), fontSize: 10.5, fontWeight: 750, letterSpacing: 1.5, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>In the name of Allah we start</span>
        <span style={{ display: 'inline-flex', opacity: 0.85 }}>{MDI.star('#F59E0B', 10)}</span>
      </span>
      <span style={{ flex: 1, height: 1, background: `linear-gradient(270deg, transparent, ${ink(0.16)})` }} />
    </div>
  );
}

/*  `fine` is a MOUSE, not a big screen: it turns on the hover controls that
    stand in for swipe-to-done and swipe-to-reschedule, which a pointer cannot
    do. Width is not this component's problem — the panel around it is a fixed
    420 on every screen bigger than a phone, so the day is drawn exactly once. */
function TodayScreen({ accent, items, nowSec, nowMin: liveNowMin, next, nextTimelineKey = null,
  closingFajr = null, timelineAnchorOffset = 0, prayed, reminders, sizeByDur, pxPerMin,
  topPad = 250, isToday = true, dayOffset = 0, overlapLabel = 'window', onOpenEdit,
  done = {}, onToggleDone, allDayRecords = [], onEditRecord, onDeleteItem,
  onRescheduleItem, fine = false }) {
  // ── long-press (heavy tap) on an event/prayer → open the grid focused there ──
  const pressTimer = useRefT(null);
  const pressPt = useRefT({ x: 0, y: 0 });
  const clearPress = () => { if (pressTimer.current) { clearTimeout(pressTimer.current); pressTimer.current = null; } };
  const gridPoint = (sourceMin, sourceOffset) => {
    const candidates = closingFajr ? [...items, closingFajr] : items;
    const fajr = candidates.find((item) => item && item.kind === 'prayer' && item.id === 'fajr'
      && item._mdSourceOffset === sourceOffset);
    const anchorOffset = fajr && Number.isFinite(fajr._mdSourceMin) && sourceMin < fajr._mdSourceMin
      ? sourceOffset - 1 : sourceOffset;
    return { focalMin: sourceMin + (sourceOffset - anchorOffset) * 1440, dayOffset: anchorOffset };
  };
  const openGridAt = (sourceMin, sourceOffset, x, y) => {
    if (!onOpenEdit) return;
    onOpenEdit({ ...gridPoint(sourceMin, sourceOffset), x, y });
  };
  // A plain tap opens the record itself (onTap); the press has to be BOTH short and
  // still, which the existing move/cancel guards already establish — if the timer is
  // still pending on pointerup, nothing disqualified this press. Inner controls (the
  // done tile, the title button, the swipe chips) own their own clicks, so a tap that
  // landed on a <button> is left to it rather than double-firing here.
  const longPress = (focalMin, onTap, sourceOffset = dayOffset) => ({
    onPointerDown: (e) => {
      pressPt.current = { x: e.clientX, y: e.clientY };
      clearPress();
      const tgt = e.currentTarget;
      tgt.style.transform = (tgt.style.transform || '').replace(/ ?scale\([^)]*\)/, '') + ' scale(0.975)';
      pressTimer.current = setTimeout(() => {
        pressTimer.current = null;
        tgt.style.transform = tgt.style.transform.replace(' scale(0.975)', '');
        if (navigator.vibrate) try { navigator.vibrate(9); } catch (err) {}
        openGridAt(focalMin, sourceOffset, e.clientX, e.clientY);
      }, 420);
    },
    onPointerMove: (e) => {
      if (pressTimer.current && Math.hypot(e.clientX - pressPt.current.x, e.clientY - pressPt.current.y) > 10) {
        clearPress(); e.currentTarget.style.transform = e.currentTarget.style.transform.replace(' scale(0.975)', '');
      }
    },
    onPointerUp: (e) => {
      const stillArmed = !!pressTimer.current;
      clearPress();
      e.currentTarget.style.transform = e.currentTarget.style.transform.replace(' scale(0.975)', '');
      if (stillArmed && onTap && !(e.target.closest && e.target.closest('button'))) onTap();
    },
    onPointerCancel: (e) => { clearPress(); e.currentTarget.style.transform = e.currentTarget.style.transform.replace(' scale(0.975)', ''); },
    onPointerLeave: (e) => { clearPress(); e.currentTarget.style.transform = e.currentTarget.style.transform.replace(' scale(0.975)', ''); },
  });

  // for other days there is no "now": past days read fully elapsed, future days fully ahead
  const nowMin = isToday && Number.isFinite(liveNowMin) ? liveNowMin
    : isToday ? nowSec / 60 : dayOffset < 0 ? Infinity : -Infinity;
  // a continuously-positioned "now" line is only truthful when the timeline is duration-scaled;
  // otherwise we fall back to the discrete between-card row
  const movingNow = isToday && sizeByDur;
  // conflicts read linearly (prayers + events, each card vs the next): seamWindow
  // labels the shared time at each seam, tuck slides the lower card under the upper
  // (glass layering), currentUids is the first current card plus any current
  // prayer, and currentUid is the one card the now-line is drawn in.
  const { seamWindow, tuck, currentUids, currentUid } = mdOverlaps(items, isToday, nowMin, dayOffset);
  const timelineKey = (item) => item && item._mdTimelineKey || item.uid || item.id;

  // measure each time-filling block's pixel box so the line lands at the exact clock position
  const colRef = useRefT(null);
  const [blocks, setBlocks] = useStateT([]);
  useLayoutEffectT(() => {
    const col = colRef.current;
    if (!movingNow || !col) { setBlocks([]); return; }
    const measure = () => {
      const ct = col.getBoundingClientRect().top;
      const arr = [...col.querySelectorAll('[data-start]')].map((el) => {
        const r = el.getBoundingClientRect();
        return { start: +el.dataset.start, end: +el.dataset.end, top: r.top - ct, bottom: r.bottom - ct };
      }).sort((a, b) => a.start - b.start);
      setBlocks(arr);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(col);
    return () => ro.disconnect();
  }, [movingNow, sizeByDur, pxPerMin, topPad, isToday, dayOffset]);

  const rows = [];
  let lastEnd = null; // end (min) of the previous scheduled block
  let clusterZ = 0;   // descending z-index within an active overlap (glass layering)
  let nowPlaced = false; // discrete current-time row drawn yet? (uniform mode only)
  const GAP_MIN = 30; // only surface gaps of 30 min+

  const sourceMinute = (axisMin) => {
    const shift = Math.floor(axisMin / 1440);
    return { offset: timelineAnchorOffset + shift, min: ((axisMin % 1440) + 1440) % 1440 };
  };
  const dateLabel = (offset) => {
    const d = mdDateForOffset(offset);
    return `${d.dow} ${d.d} ${d.monLong}`;
  };
  const pushGap = (from, to, key) => {
    if (from == null || to <= from) return;
    const gap = to - from;
    const gapIsNow = isToday && from <= nowMin && to > nowMin;
    if (!movingNow && gapIsNow && !nowPlaced) {
      rows.push(<NowLine key={`now-${key}`} accent={accent} />);
      nowPlaced = true;
    }
    if (gap < GAP_MIN) return;
    const source = sourceMinute(from);
    rows.push(
      <div key={`free-${key}`} data-start={from} data-end={to} data-now-card={gapIsNow ? '' : undefined}
        role="button" tabIndex={0}
        aria-label={`Freedom, ${mdHM(gap)}. Add something here.`}
        onClick={(e) => openGridAt(source.min, source.offset, e.clientX, e.clientY)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault();
          openGridAt(source.min, source.offset, 196, 470); } }}
        style={{ cursor: 'pointer' }}>
        <FreeTime mins={gap} from={from} to={to} isNow={gapIsNow} accent={accent} sizeByDur={sizeByDur} pxPerMin={pxPerMin} />
      </div>);
  };

  items.forEach((it, i) => {
    const itemKey = timelineKey(it);
    // Midnight is a date change, not a schedule edge: free time keeps flowing
    // across it as one continuous Freedom slot instead of splitting in two.
    if (it.kind === 'civil-boundary') return;
    if (it.kind === 'sun') {
      rows.push(<div key={itemKey}><SunMarker s={it} /></div>);
      return; // markers don't fill time
    }
    // free-time gap before this block
    pushGap(lastEnd, it.min, itemKey);
    if (it.kind === 'prayer' && it.id === 'fajr' && it._mdSourceOffset !== timelineAnchorOffset) {
      rows.push(<DayStartBreak key={`fajr-boundary-${itemKey}`} />);
    }
    // uniform mode: line right before the first upcoming block
    if (!movingNow && isToday && !nowPlaced && it.min > nowMin) {
      rows.push(<NowLine key="nowline" accent={accent} />);
      nowPlaced = true;
    }
    // The earliest-starting current card keeps the emphasis. Prayer is the one
    // exception: a prayer that begins inside it lights as a fixed day anchor.
    const isNow = isToday && !!currentUids[itemKey];
    // …but the now-line is one line, drawn in one card — the first that contains
    // the moment, which is the card mdNowY() puts it inside. Its floating time pill
    // sits where that card's duration text is, so only THAT card yields the slot.
    const hasNowLine = isToday && itemKey === currentUid;
    const isTuck = !!tuck[itemKey];          // this card slides up under its predecessor
    const seam = seamWindow[itemKey];        // {s,e} → this card overlaps the next one
    // glass layering: keep a descending z within an overlap cluster so each upper
    // pane's shadow falls onto the one tucked beneath it. Head resets to 5, every
    // tucked card steps down, plain cards drop back to the normal flow (auto).
    if (isTuck) clusterZ -= 1; else if (seam) clusterZ = 5; else clusterZ = 0;
    const sourceDay = it._mdSourceDay || mdDayKey(dayOffset);
    const sourceMin = Number.isFinite(it._mdSourceMin) ? it._mdSourceMin : it.min;
    const sourceOffset = Number.isFinite(it._mdSourceOffset) ? it._mdSourceOffset : dayOffset;
    const isDone = !!done[sourceDay + '|' + it.uid];
    const toggleDone = () => onToggleDone && onToggleDone(it.uid, sourceDay);
    // a tap opens the record in the compose sheet; prayers aren't records, so they
    // keep only the long-press → grid
    const openRecord = it.kind === 'event' && onEditRecord ? () => onEditRecord(it) : undefined;
    rows.push(
      <div key={itemKey} data-morph={it.uid} data-start={it.min} data-end={it.min + (it.dur || 0)} data-now-card={isNow ? '' : undefined}
        {...longPress(sourceMin, openRecord, sourceOffset)}
        style={{ position: 'relative', zIndex: clusterZ || 'auto', marginTop: isTuck ? -22 : undefined,
          transition: 'transform .16s cubic-bezier(.22,.61,.36,1)', transformOrigin: 'center', cursor: 'pointer',
          // an upper pane casts a real drop shadow onto the card tucked beneath it —
          // drop-shadow follows the rounded card silhouette, so the layering reads
          filter: seam ? (mdIsLight() ? 'drop-shadow(0 8px 11px rgba(40,60,110,0.16))'
            : 'drop-shadow(0 9px 14px rgba(0,0,0,0.55))') : undefined }}>
        <SwipeRow done={isDone} onToggle={toggleDone} radius={20} accent={accent} fine={fine}
        onDelete={it.kind === 'event' && onDeleteItem ? () => onDeleteItem(it) : undefined}
        onReschedule={it.kind === 'event' && onRescheduleItem ? () => onRescheduleItem(it) : undefined}>
        {it.kind === 'prayer' ?
        <PrayerBlock p={{ ...it, min: sourceMin }} accent={accent}
        isNext={isToday && itemKey === nextTimelineKey} isNow={isNow} isPast={it.min < nowMin && !(isToday && itemKey === nextTimelineKey) && !isNow}
        prayed={!!prayed[it.id]} reminder={!!reminders[it.id]} sizeByDur={sizeByDur} pxPerMin={pxPerMin} pillRow={hasNowLine && movingNow}
        done={isDone} onToggleDone={toggleDone} /> :
        <EventCard e={{ ...it, min: sourceMin }} accent={accent} isNow={isNow} isPast={it.min + (it.dur || 0) < nowMin} sizeByDur={sizeByDur} pxPerMin={pxPerMin} pillRow={hasNowLine && movingNow}
        done={isDone} onToggleDone={toggleDone} onOpen={openRecord} />}
        </SwipeRow>
        {/* shared-time chip floating at the seam, naming the overlap window */}
        {seam && overlapLabel === 'window' && <SeamLabel s={seam.s} e={seam.e}
          onOpen={onOpenEdit ? (point) => { const source = sourceMinute(seam.s);
            openGridAt(source.min, source.offset, point.x, point.y); } : null} />}
      </div>
    );
    lastEnd = lastEnd == null ? it.min + (it.dur || 0) : Math.max(lastEnd, it.min + (it.dur || 0));
  });

  // Close the final cycle with the actual Fajr returned for that civil date. The
  // composer has already shifted its position; PrayerBlock receives the original
  // clock minute so its displayed window remains an ordinary wall-clock value.
  if (lastEnd != null && closingFajr && Number.isFinite(closingFajr.min)) {
    const closingKey = timelineKey(closingFajr);
    pushGap(lastEnd, closingFajr.min, closingKey);
    rows.push(
      <div key={`closing-divider-${closingKey}`} data-screen-label="Next Fajr" aria-hidden="true"
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 4px 2px', opacity: 0.7 }}>
        <span style={{ color: ink(0.55), fontSize: 11, fontWeight: 700, letterSpacing: 1.1,
          textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{dateLabel(closingFajr._mdSourceOffset)}</span>
        <span style={{ flex: 1, height: 1, borderRadius: 2, background: ink(0.12) }} />
      </div>);
    const displayFajr = { ...closingFajr, min: closingFajr._mdSourceMin };
    rows.push(
      <div key={closingKey} data-morph="fajr-next"
        {...longPress(closingFajr._mdSourceMin, undefined, closingFajr._mdSourceOffset)}
        style={{ position: 'relative', transition: 'transform .16s cubic-bezier(.22,.61,.36,1)', transformOrigin: 'center', cursor: 'pointer' }}>
        <PrayerBlock p={displayFajr} accent={accent}
          isNext={false} isNow={false} isPast={false}
          prayed={false} reminder={!!reminders.fajr} sizeByDur={sizeByDur} pxPerMin={pxPerMin} />
      </div>);
  }

  // uniform mode: now is after everything scheduled → pin the line to the end
  if (!movingNow && isToday && !nowPlaced) rows.push(<NowLine key="nowline" accent={accent} />);

  // moving line position + label
  const yNow = movingNow ? mdNowY(blocks, nowMin) : null;
  const H = Math.floor(nowMin / 60) % 24, M = Math.floor(nowMin % 60);
  const nowLabel = `${H % 12 || 12}:${String(M).padStart(2, '0')}`;
  const ampm = H >= 12 ? 'PM' : 'AM';

  return (
    <div ref={colRef} style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 9,
      padding: `${topPad}px 16px 132px` }}>
      <AllDaySection records={allDayRecords} light={mdIsLight()} accent={accent} done={done}
        dayOffset={dayOffset} onToggleDone={onToggleDone} onEditRecord={onEditRecord}
        onDeleteItem={onDeleteItem} onRescheduleItem={onRescheduleItem} fine={fine} />
      {/* day-opening blessing — marks the start of the day, above Fajr */}
      <DayStartBreak />
      {rows}
      {yNow != null &&
      <div data-now-anchor="" style={{ position: 'absolute', left: 16, right: 16, top: 0, height: 0, zIndex: 6, pointerEvents: 'none',
        transform: `translateY(${yNow}px)`, willChange: 'transform', transition: 'transform 0.7s linear' }}>
        <div style={{ position: 'absolute', left: 0, right: 0, top: 0, transform: 'translateY(-50%)',
          display: 'flex', alignItems: 'center' }}>
          {/* left anchor dot — sits on the rail, clear of card content */}
          <span style={{ width: 9, height: 9, borderRadius: '50%', flexShrink: 0, background: accent,
            boxShadow: `0 0 0 3px ${mdRgba(accent, 0.18)}, 0 1px 5px ${mdRgba(accent, 0.6)}` }} />
          {/* hairline */}
          <span style={{ flex: 1, height: 1.5, borderRadius: 2, margin: '0 7px',
            background: `linear-gradient(90deg, ${mdRgba(accent, 0.9)}, ${mdRgba(accent, 0.55)})` }} />
          {/* time pill on the right rail */}
          <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 3, flexShrink: 0, background: accent, color: '#fff',
            fontSize: 10.5, fontWeight: 800, letterSpacing: 0.3, padding: '2.5px 7px', borderRadius: 999,
            fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', lineHeight: 1.15,
            boxShadow: `0 2px 10px ${mdRgba(accent, 0.5)}` }}>
            {nowLabel}<span style={{ fontSize: 7.5, fontWeight: 800, opacity: 0.85, letterSpacing: 0.4 }}>{ampm}</span>
          </span>
        </div>
      </div>}
    </div>);

}

Object.assign(window, { TodayScreen, TodayHeader, PrayerCountdown, WeekBar, mdDayCircle, mdLabelW, mdDateTier });
